import React, { useEffect } from 'react';
import { PhonicsActivity, PhonicsActivityTypeMode } from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { Character } from '@/components/lessonKit';
import { CarouselSlide } from '../../Carousel';

const CLAY_FONT = 'var(--clay-font)';
const FLASHCARD_FONT = 'var(--clay-font-flashcard)';

/* Accessible clay palette — white letters, all >3:1 on large bold glyphs. */
const TILE_PALETTE = [
  '#2E8B4F',
  '#2F6FDB',
  '#7E3FF2',
  '#C43B7E',
  '#D9531F',
  '#1E9AA0',
];
const TILE_ROTATIONS = [-4, 3, -2, 4, -3, 2];

const tileStyle = (
  colour: string,
  rotation: number,
  i: number,
): React.CSSProperties => ({
  flex: '0 0 auto',
  width: 78,
  height: 78,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FLASHCARD_FONT,
  fontWeight: 700,
  fontSize: 44,
  color: '#FFFFFF',
  userSelect: 'none',
  borderRadius: '44% 48% 42% 46% / 46% 42% 48% 44%',
  background: `radial-gradient(circle at 36% 26%, color-mix(in srgb, ${colour} 62%, #ffffff) 0%, ${colour} 58%, color-mix(in srgb, ${colour} 78%, #000000) 100%)`,
  boxShadow:
    'inset 0 6px 9px rgba(255,255,255,0.45), inset 0 -9px 13px rgba(0,0,0,0.26), 0 9px 16px rgba(0,0,0,0.18)',
  transform: `rotate(${rotation}deg)`,
  animation: `lp-tile-pop 0.5s ${0.1 + i * 0.08}s both ease-out`,
});

interface ClayIntroStepProps {
  activity: PhonicsActivity;
  mode: PhonicsActivityTypeMode;
  sounds: Array<string>;
  stepIdentifier: string;
  totalSteps: number;
}

/**
 * ClayIntroStep — the "lesson introduction" screen from the Claude Design
 * redesign, wired in as the opening slide of the Review-Sounds activity. A
 * coral clay field with the parrot teacher, an eyebrow chip, the big activity
 * title, a cream intro card listing the sounds to be reviewed as clay tiles,
 * and a springy "Start lesson" button that advances the carousel. Exit is
 * handled by the player chrome's existing close button, so it isn't duplicated
 * here. Registers as a normal slide so player nav / progress / metrics are
 * untouched.
 */
export const ClayIntroStep = ({
  activity,
  mode,
  sounds,
  stepIdentifier,
  totalSteps,
}: ClayIntroStepProps) => {
  const { registerSlide, scrollNextSlide } = useLessonPlayer();

  const eyebrow =
    mode === PhonicsActivityTypeMode.LEARN
      ? 'Phonics for Spelling'
      : 'Phonics for Reading';

  useEffect(() => {
    registerSlide(
      stepIdentifier,
      undefined,
      undefined,
      undefined,
      undefined,
      0,
    );
  }, [registerSlide, stepIdentifier]);

  return (
    <CarouselSlide
      identifier={stepIdentifier}
      className="theme-app relative"
      classNameInner="flex h-full w-full items-center justify-center"
      ariaLabel={`${activity.type.name} — introduction`}
      useContainer={false}
    >
      {/* coral clay field */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(125% 120% at 26% 14%, #FF7E63 0%, #F5544A 52%, #E23D3D 100%)',
        }}
      />
      {/* decorative soft blobs */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: -140,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
        }}
      />
      <div
        className="pointer-events-none absolute"
        style={{
          bottom: -180,
          left: '38%',
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.06)',
        }}
      />

      <div className="relative flex w-full flex-col items-center justify-center gap-10 px-10 md:flex-row md:items-center md:gap-10">
        {/* LEFT: parrot teacher */}
        <div
          className="flex shrink-0 items-center justify-center"
          style={{ width: 'min(38vw, 400px)' }}
        >
          <Character
            pose="teacher"
            width="100%"
            animate="idle"
            alt="Your parrot teacher"
          />
        </div>

        {/* RIGHT: intro */}
        <div
          className="flex flex-col items-start gap-5"
          style={{ flex: '0 1 640px' }}
        >
          {/* eyebrow chip */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              background: 'rgba(255,255,255,0.22)',
              padding: '9px 20px',
              borderRadius: 999,
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: '#FFE14D',
                boxShadow: 'inset 0 2px 2px rgba(255,255,255,0.7)',
              }}
            />
            <span
              style={{
                fontFamily: CLAY_FONT,
                fontWeight: 700,
                fontSize: 22,
                color: '#FFFFFF',
                letterSpacing: '0.4px',
              }}
            >
              {eyebrow}
            </span>
          </div>

          {/* title */}
          <h1
            style={{
              margin: 0,
              fontFamily: CLAY_FONT,
              fontWeight: 800,
              fontSize: 'clamp(46px,6vw,82px)',
              lineHeight: 0.98,
              color: '#FFFFFF',
              letterSpacing: '0.4px',
              textShadow: '0 4px 0 rgba(150,20,20,0.28)',
              maxWidth: 520,
            }}
          >
            {activity.type.name}
          </h1>

          {/* cream intro card */}
          <div
            style={{
              background: '#FFFDF4',
              borderRadius: 30,
              padding: '26px 30px 28px',
              boxShadow: '0 18px 34px rgba(120,15,15,0.24)',
              maxWidth: 640,
            }}
          >
            <p
              style={{
                margin: '0 0 20px',
                fontFamily: CLAY_FONT,
                fontSize: 24,
                lineHeight: 1.42,
                fontWeight: 700,
                color: '#233B22',
              }}
            >
              Let&apos;s warm up the sounds you already know. Tap Start when
              you&apos;re ready to say them out loud!
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {sounds.map((sound, i) => (
                <div
                  key={`${sound}-${i}`}
                  aria-hidden
                  style={{
                    ...tileStyle(
                      TILE_PALETTE[i % TILE_PALETTE.length],
                      TILE_ROTATIONS[i % TILE_ROTATIONS.length],
                      i,
                    ),
                    fontSize: sound.length > 2 ? 30 : 44,
                  }}
                >
                  {sound}
                </div>
              ))}
            </div>
          </div>

          {/* START button (clay green pill) */}
          <button
            type="button"
            aria-label="Start lesson"
            onClick={() => scrollNextSlide?.()}
            className="clay-press clay-btn-green"
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 14,
              border: 0,
              cursor: 'pointer',
              padding: '18px 34px',
              borderRadius: 999,
              background: 'var(--clay-green)',
              color: '#fff',
              fontFamily: CLAY_FONT,
              fontWeight: 800,
              fontSize: 26,
              letterSpacing: '0.3px',
              boxShadow:
                'inset 0 7px 10px rgba(255,255,255,0.28), inset 0 -9px 13px rgba(0,0,0,0.24), 0 10px 0 var(--clay-green-dk), 0 20px 26px -8px rgba(20,90,40,0.4)',
              animation: 'lp-start-breathe 2.2s ease-in-out infinite',
            }}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5.2v13.6L19 12z" />
            </svg>
            Start lesson
          </button>
        </div>
      </div>
    </CarouselSlide>
  );
};
